/**
 * Client app for /play/ — the spintax playground.
 *
 * Powered by the published OSS engine `@spintax/core` (the spintax-js
 * package). A single `render()` call runs the whole pipeline — comments,
 * #set/#def, conditionals, variables, plurals, enumerations, permutations, and
 * post-process — and `validate()` drives the inline error/warning overlay.
 * No framework, no dependencies beyond the browser and the bundled engine.
 */

import { render, validate, SpintaxError, type Diagnostic } from '@spintax/core';

// ── DOM refs ──────────────────────────────────────────────────

const root = document.getElementById('play');
if (!root) throw new Error('play root not found');

const el = {
  root,
  tplInput:         getEl<HTMLTextAreaElement>('tpl-input'),
  tplOverlay:       getEl<HTMLPreElement>('tpl-overlay'),
  varsRows:         getEl<HTMLDivElement>('vars-rows'),
  varsStructured:   getEl<HTMLDivElement>('vars-structured'),
  varsRaw:          getEl<HTMLTextAreaElement>('vars-raw'),
  varsTabStruct:    getEl<HTMLButtonElement>('vars-tab-structured'),
  varsTabRaw:       getEl<HTMLButtonElement>('vars-tab-raw'),
  varsAddRow:       getEl<HTMLButtonElement>('vars-add-row'),
  count:            getEl<HTMLInputElement>('count'),
  seed:             getEl<HTMLInputElement>('seed'),
  seedMode:         getEl<HTMLSelectElement>('seed-mode'),
  seedField:        getEl<HTMLLabelElement>('seed-field'),
  seedHintRandom:   getEl<HTMLParagraphElement>('seed-hint-random'),
  seedHintFixed:    getEl<HTMLParagraphElement>('seed-hint-fixed'),
  postProcess:      getEl<HTMLSelectElement>('post-process'),
  generateBtn:      getEl<HTMLButtonElement>('generate-btn'),
  clearBtn:         getEl<HTMLButtonElement>('clear-btn'),
  copyBtn:          getEl<HTMLButtonElement>('copy-link-btn'),
  output:           getEl<HTMLElement>('play-output'),
  variants:         getEl<HTMLOListElement>('variants'),
  errorBanner:      getEl<HTMLDivElement>('error-banner'),
  errorBannerMsg:   getEl<HTMLSpanElement>('error-banner-message'),
  rowTemplate:      getEl<HTMLTemplateElement>('vars-row-template'),
};

function getEl<T extends HTMLElement>(id: string): T {
  const e = document.getElementById(id);
  if (!e) throw new Error(`element #${id} not found`);
  return e as T;
}

// ── Configuration from data attributes ────────────────────────

const errorMap: Record<string, string> = JSON.parse(root.dataset.errors || '{}');
const variantLabelText = root.dataset.variantLabel || 'Variant';
const emptyOutputText = root.dataset.emptyText || '';
const defaultTemplate = root.dataset.defaultTemplate || '';
const defaultVarsRaw = root.dataset.defaultVars || '';

// ── Persistence ───────────────────────────────────────────────

// Scope persistence per language so the EN and RU pages don't clobber each
// other's default example / last-edited state.
// The page locale is also the engine's plural locale; every label comes from the page itself.
const PAGE_LANG = root.dataset.lang || 'en';
const STORAGE_KEY = `spintax-play-state-${PAGE_LANG}`;
const LINE_LABEL = root.dataset.lineLabel || 'line {n}';
const VARS_LABEL = root.dataset.varsLabel || 'Variables';
const VARS_BLOCKED = root.dataset.varsBlocked || '';

interface PersistedState {
  template: string;
  varsRaw: string;
  seed: string;
  fixed: boolean; // Result mode: Repeatable (seeded) vs Random
  count: number;
  postProcess: boolean; // render's cosmetic pass; off keeps identifiers as written (?pp=0)
}

function loadFromStorage(): PersistedState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedState>;
    if (typeof parsed.template !== 'string') return null;
    const seed = typeof parsed.seed === 'string' ? parsed.seed : '';
    return {
      template: parsed.template,
      varsRaw: typeof parsed.varsRaw === 'string' ? parsed.varsRaw : '',
      seed,
      // Saved before the Result mode existed: a seed meant the run was pinned.
      fixed: typeof parsed.fixed === 'boolean' ? parsed.fixed : seed.trim() !== '',
      count: typeof parsed.count === 'number' ? parsed.count : 5,
      postProcess: parsed.postProcess !== false, // absent in states saved before the toggle
    };
  } catch {
    return null;
  }
}

function saveToStorage(): void {
  try {
    const state: PersistedState = {
      template: el.tplInput.value,
      varsRaw: currentVarsRaw(),
      seed: el.seed.value,
      fixed: isFixed(),
      count: Number(el.count.value) || 5,
      postProcess: isPostProcess(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Quota exceeded, private mode, etc. — persistence is a nicety, not
    // a requirement. Swallow silently.
  }
}

// ── URL sharing ───────────────────────────────────────────────

function b64EncodeUtf8(s: string): string {
  // btoa only handles Latin-1; round through UTF-8 bytes for full Unicode.
  const bytes = new TextEncoder().encode(s);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function b64DecodeUtf8(s: string): string {
  const padded = s.replace(/-/g, '+').replace(/_/g, '/') + '=='.slice((s.length + 2) % 4);
  const bin = atob(padded);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

// Lift a leading run of #set/#def declarations out of a pasted-in template and into the
// Variables panel, so a shared template that carries its vocabulary inline arrives with the
// panel already laid out (blank lines inside/after the run are tolerated; the first line of
// real content ends the run — a #set deeper in the template is left where the author put it).
const LEADING_DIR = /^[ \t]*#(?:set|def)[ \t]+%\w+%[ \t]*=/;
function splitLeadingVars(template: string): { template: string; varsRaw: string } {
  const lines = template.split('\n');
  const varLines: string[] = [];
  let bodyStart = 0;
  for (let i = 0; i < lines.length; i++) {
    if (LEADING_DIR.test(lines[i])) { varLines.push(lines[i].trim()); bodyStart = i + 1; }
    else if (lines[i].trim() === '') { /* blank line within/after the block — keep scanning */ }
    else break; // first real content line ends the leading run
  }
  if (varLines.length === 0) return { template, varsRaw: '' };
  return { template: lines.slice(bodyStart).join('\n').replace(/^\n+/, ''), varsRaw: varLines.join('\n') };
}

function loadFromUrl(): PersistedState | null {
  const params = new URLSearchParams(window.location.search);
  const t = params.get('t');
  if (!t) return null;
  try {
    let template = b64DecodeUtf8(t);
    let varsRaw = params.get('v') ? b64DecodeUtf8(params.get('v') as string) : '';
    // Explicit vars (?v=) win; otherwise auto-lift any inline preset into the panel.
    if (!varsRaw.trim()) ({ template, varsRaw } = splitLeadingVars(template));
    return {
      template,
      varsRaw,
      seed:  params.get('s') || '',
      fixed: params.has('s'),
      count: Number(params.get('n')) || 5,
      // Only the literal 0 turns it off: identifier generators (catchall.in) link with pp=0.
      postProcess: params.get('pp') !== '0',
    };
  } catch {
    return null;
  }
}

function buildShareUrl(): string {
  const url = new URL(window.location.href);
  url.search = '';
  url.searchParams.set('t', b64EncodeUtf8(el.tplInput.value));
  const vars = currentVarsRaw();
  if (vars.trim()) url.searchParams.set('v', b64EncodeUtf8(vars));
  if (isFixed()) url.searchParams.set('s', seedValue());
  const n = Number(el.count.value);
  if (n && n !== 5) url.searchParams.set('n', String(n));
  if (!isPostProcess()) url.searchParams.set('pp', '0');
  return url.toString();
}

// ── Variables: raw ↔ structured ───────────────────────────────

type VarDir = 'set' | 'def';

interface VarPair { name: string; value: string; dir: VarDir }

// Mirrors the engine's own directive grammar: #set is a macro, #def rolls once.
// Which one the author wrote is carried through the structured editor untouched —
// rewriting a #def into a #set would silently change the template's semantics.
// One definition per line: [ \t], never \s, so an empty `#set %x% =` cannot swallow the next line.
const DIR_LINE = /^[ \t]*#(set|def)[ \t]+%(\w+)%[ \t]*=[ \t]*(.*)$/u;

function parseRawVars(raw: string): VarPair[] {
  const out: VarPair[] = [];
  for (const line of raw.split(/\r?\n/)) {
    const m = DIR_LINE.exec(line);
    if (m) out.push({ dir: m[1] as VarDir, name: m[2], value: m[3].trim() });
  }
  return out;
}

// Structured rows hold #set/#def only; any other line (a comment, say) would be dropped by the
// next structured edit, so such text stays in Raw.
function structuredSafe(raw: string): boolean {
  return raw.split(/\r?\n/).every((line) => line.trim() === '' || DIR_LINE.test(line));
}

function serializeVars(pairs: VarPair[]): string {
  return pairs
    .filter((p) => p.name.trim())
    .map((p) => `#${p.dir} %${p.name.trim()}% = ${p.value}`)
    .join('\n');
}

function rowsToPairs(): VarPair[] {
  const rows = el.varsRows.querySelectorAll<HTMLDivElement>('.vars__row');
  const out: VarPair[] = [];
  rows.forEach((row) => {
    const name = (row.querySelector<HTMLInputElement>('.vars__name')?.value || '').trim();
    const value = row.querySelector<HTMLInputElement>('.vars__value')?.value || '';
    const dir: VarDir =
      row.querySelector<HTMLSelectElement>('.vars__dir')?.value === 'def' ? 'def' : 'set';
    if (name || value) out.push({ name, value, dir });
  });
  return out;
}

function pairsToRows(pairs: VarPair[]): void {
  el.varsRows.innerHTML = '';
  if (pairs.length === 0) {
    addEmptyRow();
    return;
  }
  for (const p of pairs) addRow(p);
}

function addRow(pair: VarPair = { name: '', value: '', dir: 'set' }): HTMLDivElement {
  const frag = el.rowTemplate.content.cloneNode(true) as DocumentFragment;
  const row = frag.querySelector<HTMLDivElement>('.vars__row');
  if (!row) throw new Error('row template missing .vars__row');
  const nameInput = row.querySelector<HTMLInputElement>('.vars__name');
  const valueInput = row.querySelector<HTMLInputElement>('.vars__value');
  const removeBtn = row.querySelector<HTMLButtonElement>('.vars__remove');
  const dirSelect = row.querySelector<HTMLSelectElement>('.vars__dir');
  if (dirSelect) dirSelect.value = pair.dir;
  if (nameInput) nameInput.value = pair.name;
  if (valueInput) valueInput.value = pair.value;
  if (removeBtn) {
    removeBtn.addEventListener('click', () => {
      row.remove();
      if (el.varsRows.children.length === 0) addEmptyRow();
      onVarsChange();
    });
  }
  nameInput?.addEventListener('input', onVarsChange);
  valueInput?.addEventListener('input', onVarsChange);
  dirSelect?.addEventListener('change', onVarsChange);
  el.varsRows.appendChild(row);
  return row;
}

function addEmptyRow(): HTMLDivElement {
  return addRow({ name: '', value: '', dir: 'set' });
}

function currentVarsRaw(): string {
  if (isRawMode) {
    return el.varsRaw.value;
  }
  return serializeVars(rowsToPairs());
}

// ── Mode toggle (structured ↔ raw) ────────────────────────────

let isRawMode = false;

function setMode(mode: 'structured' | 'raw'): void {
  if (mode === 'structured' && isRawMode && !structuredSafe(el.varsRaw.value)) {
    showError(VARS_BLOCKED);
    return;
  }
  isRawMode = mode === 'raw';
  if (isRawMode) {
    // Flush structured rows into the raw textarea.
    el.varsRaw.value = serializeVars(rowsToPairs());
    el.varsStructured.classList.add('hidden');
    el.varsRaw.classList.remove('hidden');
    el.varsTabRaw.classList.add('is-active');
    el.varsTabRaw.setAttribute('aria-selected', 'true');
    el.varsTabStruct.classList.remove('is-active');
    el.varsTabStruct.setAttribute('aria-selected', 'false');
  } else {
    // Parse raw back into structured rows.
    pairsToRows(parseRawVars(el.varsRaw.value));
    el.varsStructured.classList.remove('hidden');
    el.varsRaw.classList.add('hidden');
    el.varsTabStruct.classList.add('is-active');
    el.varsTabStruct.setAttribute('aria-selected', 'true');
    el.varsTabRaw.classList.remove('is-active');
    el.varsTabRaw.setAttribute('aria-selected', 'false');
  }
}

// ── Overlay rendering ─────────────────────────────────────────

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// @spintax/core ≥ 0.1.3 reports precise positions for every diagnostic —
// 1-based (line, column) start plus (endLine, endColumn) end — so we underline
// the exact offending span straight from validate(), no per-code heuristics.
interface Range { start: number; end: number; code: string; severity: Diagnostic['severity'] }

function lineColToOffset(text: string, line: number, column: number): number {
  let i = 0;
  let curLine = 1;
  while (curLine < line && i < text.length) {
    if (text[i] === '\n') curLine++;
    i++;
  }
  return Math.min(i + (column - 1), text.length);
}

function computeOverlayRanges(text: string): Range[] {
  const ranges: Range[] = [];
  let diags: Diagnostic[] = [];
  // Vars-editor names go in as knownVariables so their %refs% aren't flagged;
  // inline #set names are recognised by validate() itself.
  try { diags = validate(text, { locale: PAGE_LANG, knownVariables: definedVarNames() }); } catch { /* validate is total */ }

  const undefinedNames = new Set<string>();
  for (const d of diags) {
    // Undefined vars: validate() reports the FIRST occurrence only. Collect the
    // name (from data) and underline every occurrence below — nicer in an editor.
    if (d.code === 'variable.undefined') {
      const name = d.data?.name;
      if (typeof name === 'string') undefinedNames.add(name.toLowerCase());
      continue;
    }
    const start = lineColToOffset(text, d.line, d.column);
    const end = d.endLine != null && d.endColumn != null
      ? lineColToOffset(text, d.endLine, d.endColumn)
      : start + 1;
    ranges.push({ start, end: Math.max(end, start + 1), code: d.code, severity: d.severity });
  }

  if (undefinedNames.size > 0) {
    for (const m of text.matchAll(/%(\w+)%/g)) {
      if (m.index !== undefined && undefinedNames.has(m[1].toLowerCase())) {
        ranges.push({ start: m.index, end: m.index + m[0].length, code: 'variable.undefined', severity: 'warning' });
      }
    }
  }
  return ranges;
}

function renderOverlay(text: string, ranges: Range[]): string {
  if (ranges.length === 0) return escapeHtml(text) + '\n'; // trailing \n for correct overlay height
  const sorted = [...ranges].sort((a, b) => a.start - b.start || b.end - a.end);
  let out = '';
  let i = 0;
  for (const r of sorted) {
    if (r.start < i || r.end <= r.start) continue; // skip overlaps / empty ranges
    if (r.start > i) out += escapeHtml(text.slice(i, r.start));
    const title = errorMap[r.code] || errorMap['generic'] || '';
    const cls = r.severity === 'warning' ? 'warn' : 'err';
    out += `<span class="${cls}" title="${escapeHtml(title)}">${escapeHtml(text.slice(r.start, r.end))}</span>`;
    i = r.end;
  }
  if (i < text.length) out += escapeHtml(text.slice(i));
  return out + '\n';
}

function updateTemplateOverlay(): void {
  const text = el.tplInput.value;
  el.tplOverlay.innerHTML = renderOverlay(text, computeOverlayRanges(text));
}

function syncOverlayScroll(): void {
  el.tplOverlay.scrollTop = el.tplInput.scrollTop;
  el.tplOverlay.scrollLeft = el.tplInput.scrollLeft;
}

// ── Output rendering ──────────────────────────────────────────

function clearError(): void {
  el.errorBanner.classList.add('hidden');
  el.errorBannerMsg.textContent = '';
}

function showError(message: string): void {
  el.errorBannerMsg.textContent = message;
  el.errorBanner.classList.remove('hidden');
}

function renderVariants(outputs: string[]): void {
  el.variants.innerHTML = '';
  if (outputs.length === 0) {
    const empty = document.createElement('li');
    empty.className = 'variants__empty';
    empty.textContent = emptyOutputText;
    el.variants.appendChild(empty);
    return;
  }
  outputs.forEach((text, i) => {
    const li = document.createElement('li');
    li.className = 'variant';
    const header = document.createElement('div');
    header.className = 'variant__head';
    header.textContent = `${variantLabelText} ${i + 1}`;
    const body = document.createElement('div');
    body.className = 'variant__body';
    // A variant takes the direction of its own text: Arabic reads right-to-left on any page.
    body.dir = 'auto';
    body.innerHTML = renderLeftoverOverlay(text);
    li.appendChild(header);
    li.appendChild(body);
    el.variants.appendChild(li);
  });
}

// A rendered variant should be clean. Anything left over signals a template
// problem: an undefined %var% survives verbatim, and the lenient engine emits
// a malformed block with fullwidth braces ｛…｝ (U+FF5B/U+FF5D). Underline both.
const LEFTOVER_RE = /%[A-Za-z0-9_]+%|[｛｝]/g;

function renderLeftoverOverlay(text: string): string {
  LEFTOVER_RE.lastIndex = 0;
  let out = '';
  let i = 0;
  let m: RegExpExecArray | null;
  while ((m = LEFTOVER_RE.exec(text)) !== null) {
    if (m.index > i) out += escapeHtml(text.slice(i, m.index));
    out += `<span class="err" title="${escapeHtml(errorMap['leftover'] || '')}">${escapeHtml(m[0])}</span>`;
    i = m.index + m[0].length;
  }
  if (i < text.length) out += escapeHtml(text.slice(i));
  return out;
}

// ── Generate ──────────────────────────────────────────────────

// Variable names declared in the vars editor (either mode). Used as the
// engine's knownVariables so the overlay doesn't warn about them.
function definedVarNames(): string[] {
  const pairs = isRawMode ? parseRawVars(el.varsRaw.value) : rowsToPairs();
  return pairs.map((p) => p.name.trim()).filter(Boolean);
}

// The vars editor emits canonical `#set` / `#def` lines; prepend them so the
// engine picks them up as globally-scoped definitions (its directive extraction
// is line-anchored and runs before the tree is built).
function combinedTemplate(): string {
  const vars = currentVarsRaw();
  if (!vars.trim()) return el.tplInput.value;
  return `${vars}\n${el.tplInput.value}`;
}

function humanDiagnostic(d: Diagnostic): string {
  const label = errorMap[d.code] || errorMap['generic'] || d.message;
  // Every diagnostic carries an accurate line as of @spintax/core 0.1.3.
  return `${label} — ${LINE_LABEL.replace('{n}', String(d.line))}`;
}

/**
 * One spin of the header wheel per Generate press. The wheel's own CSS animations (rotor one way,
 * the ball the other) wait paused; this runs them and brakes them to a stop over SPIN_MS, so the
 * ball comes to rest somewhere new. A press mid-spin starts the braking over at full speed. Under
 * reduced motion the library removes the animations, so there is nothing to run.
 */
const SPIN_MS = 2600;
let spinFrame = 0;
function spinWheel(): void {
  const anims = document.getElementById('play-wheel')?.getAnimations({ subtree: true }) ?? [];
  if (!anims.length) return;
  cancelAnimationFrame(spinFrame);
  const start = performance.now();
  for (const a of anims) {
    a.playbackRate = 1;
    a.play();
  }
  const brake = (now: number): void => {
    const t = Math.min(1, (now - start) / SPIN_MS);
    const rate = (1 - t) ** 2; // ease-out: fast off the button, a long slow roll at the end
    for (const a of anims) {
      if (t < 1) a.playbackRate = rate;
      else a.pause();
    }
    if (t < 1) spinFrame = requestAnimationFrame(brake);
  };
  spinFrame = requestAnimationFrame(brake);
}

/**
 * In the stacked (phone) layout the output sits under the editors, so a Generate press changed
 * nothing the reader could see. Let the wheel roll first — it is the feedback — then glide to the
 * results. Only when they are off-screen, and never against a reader who has started scrolling.
 */
const REVEAL_DELAY_MS = 1800;
const REVEAL_CANCEL = ['touchstart', 'wheel'] as const;
let revealTimer: number | undefined;
function cancelReveal(): void {
  window.clearTimeout(revealTimer);
  for (const e of REVEAL_CANCEL) window.removeEventListener(e, cancelReveal);
}
function revealOutput(): void {
  cancelReveal();
  if (el.output.getBoundingClientRect().top <= window.innerHeight / 2) return; // already in view
  const navH = document.querySelector('.nav')?.getBoundingClientRect().height ?? 0;
  const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const wheel = document.getElementById('play-wheel')?.getBoundingClientRect();
  const wheelSeen = !!wheel && wheel.bottom > navH && wheel.top < window.innerHeight;
  for (const e of REVEAL_CANCEL) window.addEventListener(e, cancelReveal, { passive: true });
  revealTimer = window.setTimeout(() => {
    cancelReveal();
    const top = el.output.getBoundingClientRect().top + window.scrollY - navH - 12;
    window.scrollTo({ top, behavior: still ? 'auto' : 'smooth' });
  }, still || !wheelSeen ? 250 : REVEAL_DELAY_MS);
}

// ── Result mode: Random vs Repeatable ─────────────────────────

function isFixed(): boolean {
  return el.seedMode.value === 'fixed';
}

/** The seed a Repeatable run uses; an empty field falls back to the placeholder's 1. */
function seedValue(): string {
  return el.seed.value.trim() || '1';
}

function setSeedMode(fixed: boolean): void {
  el.seedMode.value = fixed ? 'fixed' : 'random';
  el.seedField.hidden = !fixed;
  el.seedHintRandom.hidden = fixed;
  el.seedHintFixed.hidden = !fixed;
}

function isPostProcess(): boolean {
  return el.postProcess.value !== 'off';
}

function onGenerate(): void {
  clearError();
  const n = Math.max(1, Math.min(20, Math.floor(Number(el.count.value) || 5)));
  const tpl = combinedTemplate();
  const seedBase = isFixed() ? seedValue() : undefined;
  const postProcess = isPostProcess();

  // The engine renders leniently — a structural mistake never throws, it just
  // surfaces as a malformed block in the output. Run validate() on the template
  // text (not the combined form) so a genuine error is called out in the banner
  // with a line number that matches what the user sees in the editor.
  try {
    const firstError = validate(el.tplInput.value, { locale: PAGE_LANG }).find((d) => d.severity === 'error');
    if (firstError) {
      showError(humanDiagnostic(firstError));
    } else {
      // The vars block is not covered by the template overlay, but it accepts the
      // same directives — so a malformed #def or a duplicate name there would
      // otherwise fail silently. Validate it on its own so line numbers stay
      // meaningful, and say which editor the error is in.
      const varsText = currentVarsRaw();
      const varsError = varsText.trim()
        ? validate(varsText, { locale: PAGE_LANG }).find((d) => d.severity === 'error')
        : undefined;
      if (varsError) showError(`${VARS_LABEL}: ${humanDiagnostic(varsError)}`);
    }
  } catch { /* validate is total; ignore */ }

  const outputs: string[] = [];
  for (let i = 0; i < n; i++) {
    // Per-variant sub-seed keeps the share link reproducible while making the
    // variants differ under a single root seed. Variant 1 takes the seed as is,
    // so it matches render(template, { seed }) in the reader's own code.
    // Random mode ⇒ no seed ⇒ nondeterministic.
    const seed = seedBase === undefined ? undefined : i === 0 ? seedBase : `${seedBase}-${i}`;
    try {
      outputs.push(render(tpl, { seed, locale: PAGE_LANG, postProcess }));
    } catch (err) {
      // render() only throws on programmer error (SpintaxError), never on
      // template content — but guard so one bad variant can't blank the page.
      const msg = err instanceof SpintaxError || err instanceof Error ? err.message : String(err);
      showError(`${errorMap['generic'] || 'Error'} — ${msg}`);
      renderVariants(outputs);
      return;
    }
  }
  renderVariants(outputs);
}

// ── Actions: clear, copy-link ─────────────────────────────────

function onClear(): void {
  el.tplInput.value = defaultTemplate;
  el.varsRaw.value = defaultVarsRaw;
  if (!isRawMode) pairsToRows(parseRawVars(defaultVarsRaw));
  el.seed.value = '';
  setSeedMode(false);
  el.count.value = '5';
  el.postProcess.value = 'on';
  updateTemplateOverlay();
  renderVariants([]);
  clearError();
  saveToStorage();
}

async function onCopyLink(): Promise<void> {
  const url = buildShareUrl();
  try {
    await navigator.clipboard.writeText(url);
    flashCopied();
  } catch {
    // Clipboard API can reject (insecure context, permission). Fall back:
    // select a throwaway input so the user can Cmd/Ctrl+C manually.
    const tmp = document.createElement('input');
    tmp.value = url;
    document.body.appendChild(tmp);
    tmp.select();
    try { document.execCommand('copy'); flashCopied(); } catch { /* give up */ }
    document.body.removeChild(tmp);
  }
}

let copiedTimer: number | undefined;
function flashCopied(): void {
  el.copyBtn.classList.add('is-copied');
  if (copiedTimer !== undefined) window.clearTimeout(copiedTimer);
  copiedTimer = window.setTimeout(() => {
    el.copyBtn.classList.remove('is-copied');
    copiedTimer = undefined;
  }, 3000);
}

// ── Input handlers ────────────────────────────────────────────

function debounce<F extends (...args: never[]) => void>(fn: F, ms: number): F {
  let id: number | undefined;
  return ((...args: never[]) => {
    if (id !== undefined) window.clearTimeout(id);
    id = window.setTimeout(() => fn(...args), ms);
  }) as F;
}

const onTemplateInput = (): void => {
  updateTemplateOverlay();
  debouncedSave();
};

const onVarsChange = (): void => {
  // A var name added/removed changes which %refs% count as "known", so the
  // template overlay's undefined-variable warnings must be recomputed.
  updateTemplateOverlay();
  debouncedSave();
};

const debouncedSave = debounce(saveToStorage, 300);

// ── Init ──────────────────────────────────────────────────────

function applyState(state: PersistedState): void {
  el.tplInput.value = state.template;
  pairsToRows(parseRawVars(state.varsRaw));
  // setMode('raw') flushes the rows into the textarea, so the full text goes in after it.
  if (!structuredSafe(state.varsRaw)) setMode('raw');
  el.varsRaw.value = state.varsRaw;
  el.seed.value = state.seed;
  setSeedMode(state.fixed);
  el.count.value = String(state.count);
  el.postProcess.value = state.postProcess ? 'on' : 'off';
}

function init(): void {
  const initial = loadFromUrl() || loadFromStorage() || {
    template: defaultTemplate,
    varsRaw: defaultVarsRaw,
    seed: '',
    fixed: false,
    count: 5,
    postProcess: true,
  };
  applyState(initial);
  updateTemplateOverlay();

  // Wire events.
  el.tplInput.addEventListener('input', onTemplateInput);
  el.tplInput.addEventListener('scroll', syncOverlayScroll);
  el.varsRaw.addEventListener('input', onVarsChange);
  el.seed.addEventListener('input', onVarsChange);
  el.seedMode.addEventListener('change', () => {
    setSeedMode(isFixed());
    if (isFixed()) el.seed.focus();
    debouncedSave();
  });
  el.count.addEventListener('input', onVarsChange);
  el.postProcess.addEventListener('change', debouncedSave);
  el.varsAddRow.addEventListener('click', () => { addEmptyRow(); onVarsChange(); });
  el.varsTabStruct.addEventListener('click', () => setMode('structured'));
  el.varsTabRaw.addEventListener('click', () => setMode('raw'));
  el.generateBtn.addEventListener('click', onGenerate);
  el.generateBtn.addEventListener('click', spinWheel);
  el.generateBtn.addEventListener('click', revealOutput);
  el.clearBtn.addEventListener('click', onClear);
  el.copyBtn.addEventListener('click', () => { void onCopyLink(); });

  // Ctrl/Cmd+Enter triggers Generate from anywhere in the play area.
  el.root.addEventListener('keydown', (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      onGenerate();
    }
  });

  // First-time visitors: auto-generate once so the output panel is
  // immediately populated with meaningful content.
  if (!loadFromUrl() && !loadFromStorage()) {
    onGenerate();
  }
}

init();
